import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowLeft, Bed, Bath, Square, MapPin, Filter, X } from "lucide-react";
import { Helmet } from "react-helmet-async";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { properties } from "@/data/properties";
import { PageTransition } from "@/components/PageTransition";
import { useRegion } from "@/contexts/RegionContext";

const locations = ["All Locations", "Dubai, UAE", "Marbella, Spain", "Monaco"];

const priceRanges = [
  { label: "All Prices", min: 0, max: Infinity },
  { label: "€2M - €5M", min: 2000000, max: 5000000 },
  { label: "€5M - €10M", min: 5000000, max: 10000000 },
  { label: "€10M - €20M", min: 10000000, max: 20000000 },
  { label: "€20M+", min: 20000000, max: Infinity },
];

const parsePrice = (priceString: string): number => {
  const cleaned = priceString.replace(/[€,]/g, "");
  return parseInt(cleaned, 10);
};

const Properties = () => {
  const [selectedLocation, setSelectedLocation] = useState("All Locations");
  const [selectedPriceRange, setSelectedPriceRange] = useState(priceRanges[0]);
  const [showFilters, setShowFilters] = useState(false);
  const { formatArea, areaUnit, formatPrice } = useRegion();

  const filteredProperties = properties.filter((property) => {
    const matchesLocation =
      selectedLocation === "All Locations" || property.location === selectedLocation;
    const price = parsePrice(property.price);
    const matchesPrice =
      price >= selectedPriceRange.min && price <= selectedPriceRange.max;
    return matchesLocation && matchesPrice;
  });

  const clearFilters = () => {
    setSelectedLocation("All Locations");
    setSelectedPriceRange(priceRanges[0]);
  };

  const hasActiveFilters =
    selectedLocation !== "All Locations" || selectedPriceRange.label !== "All Prices";

  return (
    <PageTransition>
      <Helmet>
        <title>Luxury Properties for Sale | Monaco, Dubai, Italy, Spain | Agota Estate</title>
        <meta name="description" content="Browse our exclusive portfolio of luxury properties: Monaco penthouses, Dubai waterfront estates, Italian villas, Spanish mansions. €2M-€50M+. Schedule a private viewing." />
        <meta name="keywords" content="luxury properties for sale, Monaco penthouse, Dubai villa, Italian estate, Spanish mansion, buy luxury home, investment property" />
        <link rel="canonical" href="https://agotaestate.com/properties" />
      </Helmet>
      <main className="min-h-screen bg-background">
      <Navbar />

      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-b from-secondary/30 to-background">
        <div className="container mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors mb-8"
            >
              <ArrowLeft size={18} />
              <span className="font-body text-sm uppercase tracking-wider">
                Back to Home
              </span>
            </Link>

            <p className="font-body text-sm uppercase tracking-[0.3em] text-primary mb-4">
              Curated Collection
            </p>
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-light text-foreground mb-6 leading-tight">
              Our Property
              <span className="block italic font-medium">Portfolio</span>
            </h1>
            <p className="text-muted-foreground max-w-2xl text-lg leading-relaxed">
              Explore our curated selection of exceptional properties across the world's
              most prestigious locations. Each property has been personally vetted to
              meet our exacting standards.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Filters */}
      <section className="py-8 border-b border-border/50">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowFilters(!showFilters)}
                className="lg:hidden"
              >
                <Filter size={16} />
                Filters
              </Button>

              {/* Desktop Filters */}
              <div className="hidden lg:flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">Location:</span>
                  <div className="flex gap-2">
                    {locations.map((location) => (
                      <button
                        key={location}
                        onClick={() => setSelectedLocation(location)}
                        className={`px-4 py-2 text-sm rounded-full transition-all ${
                          selectedLocation === location
                            ? "bg-primary text-primary-foreground"
                            : "bg-secondary/50 text-foreground hover:bg-secondary"
                        }`}
                      >
                        {location}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="w-px h-6 bg-border" />

                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">Price:</span>
                  <div className="flex gap-2">
                    {priceRanges.map((range) => (
                      <button
                        key={range.label}
                        onClick={() => setSelectedPriceRange(range)}
                        className={`px-4 py-2 text-sm rounded-full transition-all ${
                          selectedPriceRange.label === range.label
                            ? "bg-primary text-primary-foreground"
                            : "bg-secondary/50 text-foreground hover:bg-secondary"
                        }`}
                      >
                        {range.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-4">
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  <X size={14} />
                  Clear filters
                </button>
              )}
              <span className="text-sm text-muted-foreground">
                {filteredProperties.length} properties
              </span>
            </div>
          </div>

          {/* Mobile Filters */}
          {showFilters && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="lg:hidden mt-6 space-y-4"
            >
              <div>
                <span className="text-sm text-muted-foreground block mb-2">
                  Location:
                </span>
                <div className="flex flex-wrap gap-2">
                  {locations.map((location) => (
                    <button
                      key={location}
                      onClick={() => setSelectedLocation(location)}
                      className={`px-4 py-2 text-sm rounded-full transition-all ${
                        selectedLocation === location
                          ? "bg-primary text-primary-foreground"
                          : "bg-secondary/50 text-foreground hover:bg-secondary"
                      }`}
                    >
                      {location}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <span className="text-sm text-muted-foreground block mb-2">
                  Price:
                </span>
                <div className="flex flex-wrap gap-2">
                  {priceRanges.map((range) => (
                    <button
                      key={range.label}
                      onClick={() => setSelectedPriceRange(range)}
                      className={`px-4 py-2 text-sm rounded-full transition-all ${
                        selectedPriceRange.label === range.label
                          ? "bg-primary text-primary-foreground"
                          : "bg-secondary/50 text-foreground hover:bg-secondary"
                      }`}
                    >
                      {range.label}
                    </button>
                  ))}
                </div>
              </div>
            </motion.div>
          )}
        </div>
      </section>

      {/* Properties Grid */}
      <section className="py-20">
        <div className="container mx-auto px-6 lg:px-12">
          {filteredProperties.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredProperties.map((property, index) => (
                <motion.div
                  key={property.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <Link
                    to={`/property/${property.id}`}
                    className="group block bg-card border border-border/50 rounded-2xl overflow-hidden transition-all duration-500 hover:border-primary/30 hover:shadow-luxury"
                  >
                    {/* Image */}
                    <div className="relative aspect-[4/3] overflow-hidden">
                      <img
                        src={property.image}
                        alt={property.title}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />

                      {/* Price Badge */}
                      <div className="absolute bottom-4 left-4">
                        <span className="bg-primary/90 backdrop-blur-sm text-primary-foreground px-4 py-2 rounded-full font-display text-lg font-semibold">
                          {formatPrice(property.price)}
                        </span>
                      </div>

                      {/* Featured Badge */}
                      {property.featured && (
                        <div className="absolute top-4 right-4">
                          <span className="bg-gold text-charcoal text-xs uppercase tracking-wider px-3 py-1 rounded-full font-medium">
                            Featured
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Content */}
                    <div className="p-6">
                      <div className="flex items-center gap-2 text-muted-foreground text-sm mb-2">
                        <MapPin size={14} />
                        <span>{property.location}</span>
                      </div>

                      <h3 className="font-display text-xl text-foreground mb-4 group-hover:text-primary transition-colors">
                        {property.title}
                      </h3>

                      <div className="flex items-center gap-6 text-sm text-muted-foreground">
                        <div className="flex items-center gap-1.5">
                          <Bed size={16} />
                          <span>{property.beds} Beds</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <Bath size={16} />
                          <span>{property.baths} Baths</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <Square size={16} />
                          <span>{formatArea(property.sqm)} {areaUnit}</span>
                        </div>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          ) : (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-20"
            >
              <p className="text-muted-foreground text-lg mb-4">
                No properties match your current filters.
              </p>
              <Button variant="outline" onClick={clearFilters}>
                Clear Filters
              </Button>
            </motion.div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-secondary/30">
        <div className="container mx-auto px-6 lg:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="font-display text-3xl md:text-4xl text-foreground mb-4">
              Looking for Something Specific?
            </h2>
            <p className="text-muted-foreground max-w-xl mx-auto mb-8">
              Our portfolio extends beyond what's shown here. Share your requirements
              and we'll source the perfect property for you.
            </p>
            <Button variant="luxury" size="xl" asChild>
              <Link to="/#contact">Request Custom Search</Link>
            </Button>
          </motion.div>
        </div>
      </section>

      <Footer />
      </main>
    </PageTransition>
  );
};

export default Properties;